
import Vue from 'vue';
import Component from 'vue-class-component';
import {namespace} from 'vuex-class';
import {Watch} from 'vue-property-decorator';
import {IOnOffHandlers} from 'bonomo/dist';
import {IErrors} from '@/model/IErrors';
import {IOrder, IOrderService, EOrderPaymentStatuses, EOrderStatuses} from '@/model/Order/orderInterfaces';
import {IEligibleService} from '@/store/modules/orderModule';
import {flattenErrors} from '@/helpers/errorsHelper';
import {UrlHelper} from '@/helpers/UrlHelper';
import {PermissionHelper} from '@/helpers/PermissionHelper';
import {EUserPermissions} from '@/model/EUserPermissions';
import {EPermissionActions} from '@/model/EPermissionActions';
import {IChipStyle} from '@/model/chipStyleInterfaces';
import {ICell} from '@/model/Locker/lockerInterfaces';
import {TranslationHelper} from '@/helpers/TranslationHelper';
import OrderChip from '@/components/shared/chips/OrderChip.vue';
import OrderStatusChip from '@/components/shared/chips/OrderStatusChip.vue';
import PaymentStatusChip from '@/components/shared/chips/PaymentStatusChip.vue';
import SimpleConfirmationDialog from '@/components/shared/SimpleConfirmationDialog.vue';
import OrderStatusStepper from '@/components/order/order/OrderStatusStepper.vue';

const orderModule = namespace('orderModule');

interface IEditedLine {
  quantity: number;
  item_price: number;
}

interface IAddServiceForm {
  serviceId: number | null;
  quantity: number;
  price: string;
}

@Component({
  components: {
    OrderStatusChip,
    PaymentStatusChip,
    SimpleConfirmationDialog,
    OrderStatusStepper,
  },
})
export default class OrderEdit extends Vue {
  @orderModule.Getter('order')
  protected order!: IOrder;
  @orderModule.Getter('errors')
  protected errors!: IErrors;
  @orderModule.Getter('availableServices')
  protected availableServices!: IEligibleService[];
  @orderModule.Getter('lockerCellSizes')
  protected lockerCellSizes!: any[];
  @orderModule.Getter('eligibleServiceProviders')
  protected eligibleServiceProviders!: any[];

  protected loading: boolean = true;
  protected submitting: boolean = false;
  protected showDeleteServiceDialog: boolean = false;
  protected pendingDeleteServiceId: number | null = null;
  protected editedLines: { [id: number]: IEditedLine } = {};
  protected showAddServiceDialog: boolean = false;
  protected loadingAvailableServices: boolean = false;
  protected addServiceForm: IAddServiceForm = { serviceId: null, quantity: 1, price: '' };
  protected addServiceTab: number = 0;
  protected genericArticleForm: {description: string; quantity: number; price: number; serviceProviderId: number|null; lockerCellSizeId: number|null} = {
    description: '',
    quantity: 1,
    price: 0,
    serviceProviderId: null,
    lockerCellSizeId: null,
  };
  protected genericCellSizeError: string = '';
  protected genericPriceError: string = '';
  protected statusForm: {status: string | null; payment_status: string | null} = {status: null, payment_status: null};
  private subscribes: IOnOffHandlers = {};

  get orderId(): number {
    return parseInt(this.$route.params.orderId, 10);
  }

  get isAdmin(): boolean {
    const userData = this.$store.getters['authModule/userData'];
    return userData && PermissionHelper.hasPermissionAny(userData, EUserPermissions.PERMISSION_ADMINISTRATOR);
  }

  get canEditServices(): boolean {
    if (!this.order) return false;
    return !this.order.locked || this.$canAction(EPermissionActions.ACTION_ORDERS_UPDATE);
  }

  get canPay(): boolean {
    if (!this.order) return false;
    const unpaid = [EOrderPaymentStatuses.status_payment_unpaid, EOrderPaymentStatuses.status_payment_declined];
    return unpaid.indexOf(this.order.payment_status) !== -1;
  }

  get canUnPay(): boolean {
    if (!this.order) return false;
    return this.order.payment_status === EOrderPaymentStatuses.status_payment_paid;
  }

  get hasCell(): boolean {
    return !!(this.order && (this.order.currentCell || this.order.pickupCell));
  }

  get currentCell(): ICell {
    return (this.order.currentCell || this.order.pickupCell) as ICell;
  }

  get orderChipStyle(): IChipStyle {
    return OrderChip.chipStyleOf(this.order ? this.order.type : '');
  }

  get flatErrors(): string[] {
    return flattenErrors(this.errors);
  }

  get goToOldEdit(): string {
    return UrlHelper.mayorOrderEdit(this.orderId);
  }

  get addServiceItems(): Array<{id: number; label: string}> {
    return this.availableServices.map((s) => {
      const name = (typeof s.name === 'object' && s.name !== null)
        ? TranslationHelper.translateField(s.name as any) || String(s.name)
        : String(s.name || '');
      const price = s.on_sale && s.sale_price != null ? s.sale_price : s.price;
      return {
        id: s.id,
        label: name + ' — ' + price + ' €',
      };
    });
  }

  get addServiceDefaultPrice(): string {
    if (!this.addServiceForm.serviceId) return '';
    const svc = this.availableServices.find((s) => s.id === this.addServiceForm.serviceId);
    if (!svc) return '';
    const p = svc.on_sale && svc.sale_price != null ? svc.sale_price : svc.price;
    return String(p);
  }

  get lockerCellSizesWithNull(): any[] {
    const noOption = { id: null, name: String(this.$t('orderCreate.step3.genericArticleNoCompartment')) };
    const sizes = (Array.isArray(this.lockerCellSizes) ? this.lockerCellSizes : []).map(function(s: any) {
      const n = s.name;
      return {
        id: s.id,
        name: (typeof n === 'object' && n !== null) ? (TranslationHelper.translateField(n) || String(n)) : String(n || ''),
      };
    });
    return [noOption].concat(sizes);
  }

  get availableServiceProviders(): any[] {
    return Array.isArray(this.eligibleServiceProviders) ? this.eligibleServiceProviders : [];
  }

  // Approximates Order::canBeCompleted() (hasBeenPaid() || isInvoiceOrder()) — the invoice check
  // there is a user-level billing flag not exposed to the frontend order payload, so this uses
  // payment_status as a proxy. The backend re-validates authoritatively regardless.
  protected get canBeCompletedApprox(): boolean {
    const paidLike = [
      EOrderPaymentStatuses.status_payment_paid,
      EOrderPaymentStatuses.status_payment_unpaid_invoice,
      EOrderPaymentStatuses.status_payment_paid_invoice,
    ];
    const eligibleStatuses = [
      EOrderStatuses.status_order_in_route_delivery,
      EOrderStatuses.status_order_sp_delivered,
      EOrderStatuses.status_order_assembled,
    ];
    return paidLike.indexOf(this.order.payment_status) !== -1 && eligibleStatuses.indexOf(this.order.status) !== -1;
  }

  protected get isLockerOrderApprox(): boolean {
    return !!this.order.pickupLocation && this.order.pickupLocation.type === 'locker';
  }

  // Mirrors resources/views/partials/forms/elements/orders/statuses.blade.php exactly:
  // only the current status plus the 1-2 concrete one-step transitions that are actually valid.
  protected get statusOptions(): Array<{text: string; value: string}> {
    const options = [{
      value: this.order.status,
      text: String(this.$t('orders.order.statuses.' + this.order.status)) + ' (' + this.$t('orders.order.forceStatus.current') + ')',
    }];

    if (this.canBeCompletedApprox) {
      options.push({
        value: EOrderStatuses.status_order_completed,
        text: String(this.$t('orders.order.statuses.status_order_completed')),
      });
    }

    if ([EOrderStatuses.status_order_awaiting_collect, EOrderStatuses.status_order_in_route_collect].indexOf(this.order.status) !== -1) {
      options.push({
        value: EOrderStatuses.status_order_sp_collected,
        text: String(this.$t('orders.order.statuses.status_order_sp_collected')),
      });
    } else if (
      this.isLockerOrderApprox &&
      [EOrderStatuses.status_order_assembled, EOrderStatuses.status_order_in_route_delivery].indexOf(this.order.status) !== -1
    ) {
      options.push({
        value: EOrderStatuses.status_order_sp_delivered,
        text: String(this.$t('orders.order.statuses.status_order_sp_delivered')),
      });
    }

    return options;
  }

  protected get paymentStatusOptions(): Array<{text: string; value: string}> {
    return (Object.values(EOrderPaymentStatuses) as string[]).map((value) => ({
      value,
      text: String(this.$t('orders.order.paymentStatuses.' + value)),
    }));
  }

  protected get canForceStatus(): boolean {
    return this.$canAction(EPermissionActions.ACTION_ORDERS_CREATE);
  }

  protected get canUpdateStatus(): boolean {
    return this.canForceStatus &&
      (this.statusForm.status !== this.order.status || this.statusForm.payment_status !== this.order.payment_status);
  }

  serviceName(item: IOrderService): string {
    if (item.service && item.service.name) {
      if (typeof item.service.name === 'object') {
        return TranslationHelper.translateField(item.service.name as any) || item.name || '—';
      }
      return item.service.name as any || item.name || '—';
    }
    return item.name || '—';
  }

  lineSubtotal(item: IOrderService): string {
    if (!this.editedLines[item.id]) return item.sub_total != null ? String(item.sub_total) : '—';
    const qty = this.editedLines[item.id].quantity;
    const price = this.editedLines[item.id].item_price;
    return (qty * price).toFixed(2);
  }

  isLineDirty(item: IOrderService): boolean {
    if (!this.editedLines[item.id]) return false;
    return this.editedLines[item.id].quantity !== item.quantity ||
           this.editedLines[item.id].item_price !== item.item_price;
  }

  private initEditedLines() {
    if (!this.order || !this.order.services) return;
    const lines: { [id: number]: IEditedLine } = {};
    this.order.services.forEach((s) => {
      lines[s.id] = { quantity: s.quantity, item_price: s.item_price };
    });
    this.editedLines = lines;
  }

  @Watch('$route')
  onRouteChanged() {
    this.loadData();
  }

  created() {
    this.subscribes = (this as any).$ons({
      'backend.call.OK.orderLoaded': this.onOrderLoaded,
      'backend.call.OK.availableServicesLoaded': this.onAvailableServicesLoaded,
      'backend.call.KO': this.onError,
    });
    this.loadData();
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  protected onUpdateStatus() {
    if (!this.canUpdateStatus) return;
    this.submitting = true;
    this.$store.dispatch('orderModule/updateOrderStatus', {
      orderId: this.orderId,
      status: this.statusForm.status !== this.order.status ? this.statusForm.status : null,
      payment_status: this.statusForm.payment_status !== this.order.payment_status ? this.statusForm.payment_status : null,
    });
  }

  protected onPay() {
    this.submitting = true;
    this.$store.dispatch('orderModule/payOrder', this.orderId);
  }

  protected onUnPay() {
    this.submitting = true;
    this.$store.dispatch('orderModule/unPayOrder', this.orderId);
  }

  protected onReleaseCell() {
    this.submitting = true;
    this.$store.dispatch('orderModule/releaseCell', this.orderId);
  }

  protected onAssignNewCode() {
    this.submitting = true;
    this.$store.dispatch('orderModule/assignNewCode', this.orderId);
  }

  protected onSaveServiceLine(item: IOrderService) {
    if (!this.editedLines[item.id]) return;
    this.submitting = true;
    this.$store.dispatch('orderModule/updateOrderService', {
      orderId: this.orderId,
      orderServiceId: item.id,
      quantity: this.editedLines[item.id].quantity,
      item_price: this.editedLines[item.id].item_price,
    });
  }

  protected confirmDeleteService(item: IOrderService) {
    this.pendingDeleteServiceId = item.id;
    this.showDeleteServiceDialog = true;
  }

  protected onDeleteService() {
    if (this.pendingDeleteServiceId === null) return;
    this.submitting = true;
    this.$store.dispatch('orderModule/deleteOrderService', {
      orderId: this.orderId,
      orderServiceId: this.pendingDeleteServiceId,
    });
    this.pendingDeleteServiceId = null;
  }

  private loadData() {
    this.loading = true;
    this.$store.dispatch('orderModule/fetchOrder', this.orderId);
  }

  private onOrderLoaded() {
    this.loading = false;
    this.submitting = false;
    this.showDeleteServiceDialog = false;
    this.showAddServiceDialog = false;
    this.initEditedLines();
    this.statusForm = {status: this.order.status, payment_status: this.order.payment_status};
  }

  private onAvailableServicesLoaded() {
    this.loadingAvailableServices = false;
  }

  private onError() {
    this.submitting = false;
    this.loading = false;
    this.loadingAvailableServices = false;
  }

  protected openAddServiceDialog() {
    this.addServiceForm = { serviceId: null, quantity: 1, price: '' };
    this.loadingAvailableServices = true;
    this.$store.dispatch('orderModule/fetchOrderAvailableServices', this.orderId);
    this.$store.dispatch('orderModule/fetchLockerCellSizes', { orderId: this.orderId });
    this.$store.dispatch('orderModule/fetchEligibleServiceProviders', { orderId: this.orderId });
    this.showAddServiceDialog = true;
  }

  protected onAddServiceDialogToggle(open: boolean) {
    if (!open) {
      this.addServiceForm = { serviceId: null, quantity: 1, price: '' };
      this.genericArticleForm = { description: '', quantity: 1, price: 0, serviceProviderId: null, lockerCellSizeId: null };
      this.genericCellSizeError = '';
      this.genericPriceError = '';
      this.addServiceTab = 0;
    }
  }

  protected onAddService() {
    if (!this.addServiceForm.serviceId) return;
    this.submitting = true;
    const price = this.addServiceForm.price !== '' ? parseFloat(this.addServiceForm.price) : undefined;
    this.$store.dispatch('orderModule/addOrderService', {
      orderId: this.orderId,
      serviceId: this.addServiceForm.serviceId,
      quantity: this.addServiceForm.quantity,
      price,
    });
  }

  protected onAddGenericArticle() {
    if (!this.genericArticleForm.description || !this.genericArticleForm.serviceProviderId) return;
    if (!this.genericArticleForm.price || this.genericArticleForm.price <= 0) {
      this.genericPriceError = String(this.$t('common.required'));
      return;
    }
    this.genericPriceError = '';
    const hasCellSizeOptions = Array.isArray(this.lockerCellSizes) && this.lockerCellSizes.length > 0;
    if (hasCellSizeOptions && this.genericArticleForm.lockerCellSizeId === null) {
      this.genericCellSizeError = String(this.$t('orderCreate.step3.genericArticleMinCompartmentRequired'));
      return;
    }
    this.genericCellSizeError = '';
    this.submitting = true;
    this.$store.dispatch('orderModule/addOrderService', {
      orderId: this.orderId,
      serviceId: 0,
      quantity: this.genericArticleForm.quantity,
      price: this.genericArticleForm.price,
      customName: this.genericArticleForm.description,
      lockerCellSizeId: this.genericArticleForm.lockerCellSizeId,
    });
  }

  protected onOpenAddServiceDialog() {
    this.addServiceTab = 0;
    this.genericArticleForm = { description: '', quantity: 1, price: 0, serviceProviderId: null, lockerCellSizeId: null };
    this.openAddServiceDialog();
  }
}
